import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, CircularProgress, Paper, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../services/providers/authProvider";
import { AccessGateContext } from "../../context/accessGateContext";
import {
  getImpersonateHandshakePostMessageTarget,
  isImpersonatePostMessageOriginAllowed,
} from "../../config/impersonateConfig";
import { AuthSessionManager } from "../../auth/AuthSessionManager";
import Logo from "../../assets/image/logo.png";
import LoginBackground from "../Shared/loginBackground";

const MIN_SPLASH_MS = 2000;

function extractTokenFromMessage(data) {
  if (typeof data === "string") return data.trim();
  if (data && typeof data === "object" && typeof data.token === "string") {
    return data.token.trim();
  }
  return null;
}

const ImpersonateLogin = () => {
  const { loginWithToken } = useAuth() || {};
  const { allowVendorAreaAccess } = useContext(AccessGateContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [error, setError] = useState("");
  const startedAtRef = useRef(Date.now());
  const doneRef = useRef(false);

  const queryToken = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("token");
    return t?.trim() || null;
  }, [location.search]);

  useLayoutEffect(() => {
    AuthSessionManager.logout();
  }, []);

  useEffect(() => {
    const goDashboardWithMinimumSplash = () => {
      const elapsed = Date.now() - startedAtRef.current;
      const delay = Math.max(0, MIN_SPLASH_MS - elapsed);
      globalThis.setTimeout(() => {
        allowVendorAreaAccess();
        navigate("/dashboard", { replace: true });
      }, delay);
    };

    const tryImpersonatedLogin = (token) => {
      if (doneRef.current || !token) return false;
      try {
        const principal = loginWithToken(token);
        const role = principal?.role?.toLowerCase?.() ?? "";
        if (role !== "vendor") {
          setError("Token non valido per accesso vendor.");
          return false;
        }
        doneRef.current = true;
        goDashboardWithMinimumSplash();
        return true;
      } catch (err) {
        const msg = err?.message === "TOKEN_EXPIRED"
          ? "Token impersonificato scaduto."
          : "Token impersonificato non valido o scaduto.";
        setError(msg);
        return false;
      }
    };

    const handleMessage = (event) => {
      if (!isImpersonatePostMessageOriginAllowed(event.origin)) {
        return;
      }
      const token = extractTokenFromMessage(event?.data);
      tryImpersonatedLogin(token);
    };

    globalThis.addEventListener("message", handleMessage);
    const opener = globalThis.opener;
    if (opener && typeof opener.postMessage === "function") {
      const targetOrigin = getImpersonateHandshakePostMessageTarget();
      if (targetOrigin) {
        opener.postMessage({ type: "wcp:vendor:impersonate-ready" }, targetOrigin);
      }
    }

    if (queryToken) {
      tryImpersonatedLogin(queryToken);
    }

    return () => globalThis.removeEventListener("message", handleMessage);
  }, [allowVendorAreaAccess, loginWithToken, navigate, queryToken]);

  return (
    <LoginBackground
      title="Wow - Accesso Impersonificato Vendor"
      containerSx={{ width: "100%", p: 2 }}
    >
      <Paper
        sx={{
          width: "100%",
          zIndex: 2,
          maxWidth: 520,
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          textAlign: "center",
          boxShadow: "2px 2px 18px 5px rgba(0, 0, 0, 0.28)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <img src={Logo} alt="Logo Wow che prezzi" height="82" />
        </Box>

        <Typography
          variant="h5"
          sx={{ fontFamily: "Rubik", fontWeight: 700, color: "#0b3d87", mb: 1 }}
        >
          Accesso impersonificato in corso...
        </Typography>
        <Typography sx={{ fontFamily: "Rubik", color: "#5f6b7a", mb: 3 }}>
          Stiamo autenticando la sessione vendor. Attendi qualche secondo.
        </Typography>

        <CircularProgress sx={{ color: "#0b3d87" }} />

        {error ? (
          <Alert severity="error" sx={{ mt: 3, textAlign: "left" }}>
            {error}
          </Alert>
        ) : null}
      </Paper>
    </LoginBackground>
  );
};

export default ImpersonateLogin;
